<template>
    <div>
        <page-title
            :title="$t('deletedRecords.deletedDocuments')"
            :showTitle="false"
        />
        <div class="card">
            <div class="card-header bg-primary text-white">
                <div class="h5 m-0">{{ $t('deletedRecords.deletedDocuments') }}</div>
            </div>
            <div class="card-body">
                <div class="btn-toolbar justify-content-between mt-0 mb-2" role="toolbar">
                    <div class="form-inline col-6" />
                    <div class="input-group">
                        <input
                            v-model="searchQuery"
                            type="text"
                            v-on:keyup.enter.stop="massSearch"
                            v-on:keyup.esc.stop="clearFilters"
                            class="form-control"
                            aria-label="Mass search input"
                            aria-describedby="massSearch"
                        />
                        <button
                            class="btn btn-secondary"
                            @click.stop="massSearch"
                        >
                            <i class="fas fa-search"></i>
                            {{ $t('buttons.search.btnText') }}
                        </button>
                        <button
                            class="btn btn-secondary"
                            @click.stop="clearFilters"
                        >
                            <i class="fas fa-times"></i>
                            {{ $t('buttons.clearFilters.btnText') }}
                        </button>
                    </div>
                </div>

                <kendo-datasource ref="datasource1"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total '"
                                  :schema-data="'data'"
                                  :page-size='defaultGridRows'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable">
                    <kendo-grid-column
                        :template="btnsTemplate"
                        field="btnsColumn"
                        :title="' '"
                        :reorderable="false"
                        :sortable="false"
                        :filterable="false"
                        :lockable="false"
                        :locked="false"
                        :width="80">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'docTypeName'"
                                       :title="$t('deletedDocument.headers.docType')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'fileName'"
                                       :title="$t('deletedDocument.headers.fileName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'description'"
                                       :title="$t('deletedDocument.headers.description')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventId'"
                                       :title="$t('deletedDocument.headers.eventId')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventName'"
                                       :title="$t('deletedDocument.headers.eventName')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'eventCode'"
                                       :title="$t('deletedDocument.headers.eventCode')">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'deletedOn'"
                                       :title="$t('deletedDocument.headers.deletedOn')"
                                       :format="customDatetimeFormat">
                    </kendo-grid-column>
                    <kendo-grid-column :field="'deletedBy'"
                                       :title="$t('deletedDocument.headers.deletedBy')">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import store from '@/store/store';
import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
import PageTitle from '@/components/common/PageTitle';
import { mapGetters } from "vuex";

export default {
    components: { PageTitle },
    data() {
        return {
            defaultGridRows: this.$store.getters.defaultGridRows,
            searchQuery: '',
            excludedColumnsForSearch: [],
            schemaModelFields: {
                docTypeName: {
                    name: 'docTypeName',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true
                },
                fileName: {
                    name: 'fileName',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                description: {
                    name: 'description',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                eventId: {
                    name: 'eventId',
                    type: 'number',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                eventName: {
                    name: 'eventName',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                eventCode: {
                    name: 'eventCode',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                deletedOn: {
                    name: 'deletedOn',
                    type: 'date',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
                deletedBy: {
                    name: 'deletedBy',
                    type: 'string',
                    editable: false,
                    nullable: true,
                    visible: true,
                },
            },
            transportSettings: {
                read: {
                    url: `${store.getters.baseUrl}/api/document/listDeleted`,
                    dataType: 'json',
                    type: 'POST',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend
                },
                parameterMap: this.parameterMap
            },
            dataReferences: "",
            showDataReferences: false,
        }
    },
    computed: {
        ...mapGetters(['dateTimeFormat', 'claims', 'gridOptions']),
        token: function () {
            return store.getters.authToken;
        },
        customDatetimeFormat() {
            return `{0:${this.dateTimeFormat}}`;
        },
    },
    methods: {
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t('notifications.error'),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: 'danger',
                time: this.$store.getters.notificationDuration,
            });
        },
        parameterMap: function (options, operation) {
            if (operation !== "read" && options.models) {
                return { models: kendo.stringify(options.models) };
            } else {
                return kendo.stringify(options);
            }
        },
        beforeSend: function (req) {
            if (this.token) {
                req.setRequestHeader('Authorization', 'Bearer ' + this.token);
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    parentComponent: this.$refs.grid,
                    btns: [
                        {
							name: 'download',
							text: '',
							tooltip: this.$t('buttons.download.tooltipText'),
							class: 'btn btn-success',
							icon: 'fas fa-download',
							show: true,
							clickHandler: this.downloadDoc
						},
                        {
                            name: "undelete",
                            text: "",
                            tooltip: this.$t("buttons.undelete.tooltipText"),
                            class: "btn btn-success",
                            icon: "fas fa-check",
                            show: this.claims.deletedRecords.update,
                            clickHandler: this.undeleteBtnClick,
                        },
                    ],
                },
            };
        },
        refresh() {
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.read();
            }
        },
        clearFilters() {
            this.searchQuery = "";
            const grid = this.$refs.grid.kendoWidget();
            if (grid) {
                grid.dataSource.sort({});
                grid.dataSource.page(1);
                grid.dataSource.aggregate([]);
                grid.dataSource.filter({});
            }
        },
        massSearch() {
            if (!this.searchQuery) {
                this.$vs.notify({
                    title: this.$t("buttons.search.btnText"),
                    text: this.$t("emptySearchFilter"),
                    color: "warning",
                    time: this.$store.getters.notificationDuration,
                });

                return;
            }

            let grid = this.$refs.grid.kendoWidget();
            if (grid) {
                let filter = this.$helpers.getMassFilterForGrid(
                    grid,
                    this.searchQuery,
                    this.excludedColumnsForSearch
                );
                if (filter) {
                    grid.dataSource.filter(filter);
                }
            }
        },
        undeleteBtnClick(args) {
            kendo
                .confirm(`${this.$t('buttons.undelete.confirmationText1', { name: args.fileName })}`)
                .then(
                    function () {
                        this.undeleteVenues(args.docType, args.docId);
                    }.bind(this)
                );
        },
        undeleteVenues(docType, docId) {
            this.$http
                .delete("/api/document/undelete", { params: { docType: docType, id: docId } })
                .then((response) => {
                    this.refresh();

                    this.$vs.notify({
                        title: '',
                        text: this.$helpers.responseMessage(response),
                        color: 'success',
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            'request.response.status.' +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration,
                    });
                });
        },
        downloadDoc(e) {
            const downloadUri = this.$store.getters.baseUrl + `/api/file/download/?id=${e.docId}&type=${e.docTypeCode}&hash=${e.hash}`;
            this.loading = true;
            this.$http.get(downloadUri, { responseType: 'blob' })
                .then(response => {
                    const blob = new Blob([response.data]);
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', e.fileName); // or any other extension
                    document.body.appendChild(link);
                    link.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch(err => {
                    this.$vs.notify({
                        color: 'danger',
                        title: this.$t('notifications.error'),
                        text: this.$helpers.errorText(err)
                    })
                })
                .then(() => { this.loading = false; });
		},
    },
}
</script>
